import { COMPANY_ID } from "@constants/auth.constant";
import { AuthService } from "@services/api/Auth.service";
import { LocalStorageService } from "@services/utils/localStorage.service";
import { useQuery } from "@tanstack/react-query";
import { ENV } from "config/ENV.config";
import { createContext, ReactNode, useContext, useLayoutEffect, useMemo } from "react";
import {
  BLACK,
  DARK_BG,
  hexToRgb,
  luminance,
  mix,
  readableOn,
  RGB,
  rgbList,
  rgbToHex,
  textOn,
  WHITE,
} from "utility/color";

/**
 * Per-company branding for the console. verify-token's companylist carries each company's
 * logoUrl, themeColor, asideColor and asideTextColor, and the active one is the `cid` in storage.
 *
 * Colours are written as inline custom properties on <html>, so they beat both the `:root` and
 * the `[data-bs-theme=dark]` stylesheet rules. erpboi-theme.scss maps them onto Bootstrap's and
 * AdminLTE's variables, with stock AdminLTE values as the fallback, so a company without branding
 * gets plain AdminLTE. Everything is removed again on unmount: the signed-out pages never carry a
 * company's colours.
 */

export const BRANDING_QUERY_KEY = ["portal-branding"];

type Company = {
  companyId?: string;
  companyName?: string;
  roleKey?: string;
  logoUrl?: string;
  themeColor?: string;
  asideColor?: string;
  asideTextColor?: string;
};

type BrandingContextType = {
  companyName: string;
  /** Absolute URL of the company logo, or "" when it has none. */
  logoUrl: string;
  /** The sidebar's colour scheme: dark unless the company painted it a light colour. */
  sidebarTheme: "light" | "dark";
};

const BrandingContext = createContext<BrandingContextType>({ companyName: "", logoUrl: "", sidebarTheme: "dark" });

export const useBranding = () => useContext(BrandingContext);

const SIDEBAR_CLASS = "erp-branded-sidebar";

const setVars = (vars: Record<string, string>) => {
  const style = document.documentElement.style;
  Object.entries(vars).forEach(([k, v]) => style.setProperty(k, v));
  return () => Object.keys(vars).forEach((k) => style.removeProperty(k));
};

const primaryVars = (brand: RGB): Record<string, string> => {
  const hover = mix(brand, BLACK, 0.15);
  const ink = readableOn(brand, WHITE, BLACK);
  const inkHover = mix(ink, BLACK, 0.2);
  const inkDark = readableOn(brand, DARK_BG, WHITE);
  const inkDarkHover = mix(inkDark, WHITE, 0.2);
  return {
    "--erp-brand": rgbToHex(brand),
    "--erp-brand-rgb": rgbList(brand),
    "--erp-brand-hover": rgbToHex(hover),
    "--erp-brand-active": rgbToHex(mix(brand, BLACK, 0.2)),
    "--erp-brand-contrast": rgbToHex(textOn(brand)),
    "--erp-brand-ink": rgbToHex(ink),
    "--erp-brand-ink-rgb": rgbList(ink),
    "--erp-brand-ink-hover": rgbToHex(inkHover),
    "--erp-brand-ink-hover-rgb": rgbList(inkHover),
    "--erp-brand-subtle": rgbToHex(mix(brand, WHITE, 0.8)),
    "--erp-brand-border-subtle": rgbToHex(mix(brand, WHITE, 0.6)),
    "--erp-brand-ink-dark": rgbToHex(inkDark),
    "--erp-brand-ink-dark-rgb": rgbList(inkDark),
    "--erp-brand-ink-dark-hover": rgbToHex(inkDarkHover),
    "--erp-brand-ink-dark-hover-rgb": rgbList(inkDarkHover),
    "--erp-brand-subtle-dark": rgbToHex(mix(brand, DARK_BG, 0.8)),
    "--erp-brand-border-subtle-dark": rgbToHex(mix(brand, DARK_BG, 0.5)),
  };
};

// Every sidebar state is built from the company's OWN bg/text pair, never from the accent: a
// company often paints its sidebar in its brand colour, and accent text on it disappears (hrm hit
// this on stage with a #14a4de sidebar). The active item is the inverse pill.
const sidebarVars = (bg: RGB, text: RGB | null): Record<string, string> => {
  const fg = text ?? textOn(bg);
  const tint = (alpha: number) => `rgba(${rgbList(fg)}, ${alpha})`;
  return {
    "--erp-sidebar-bg": rgbToHex(bg),
    "--erp-sidebar-text": rgbToHex(fg),
    "--erp-sidebar-muted": tint(0.65),
    "--erp-sidebar-hover-bg": tint(0.12),
    // The guide line a group's items hang off (erpboi-theme: .erp-nav-children).
    "--erp-sidebar-guide": tint(0.4),
    "--erp-sidebar-active-bg": rgbToHex(fg),
    "--erp-sidebar-active-text": rgbToHex(bg),
  };
};

const parseCompanies = (raw: unknown): Company[] => {
  if (Array.isArray(raw)) return raw as Company[];
  if (typeof raw !== "string" || !raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: () => AuthService.verifyToken(ENV.productCode),
    staleTime: 5 * 60 * 1000,
  });

  const active = useMemo<Company | undefined>(() => {
    const companies = parseCompanies(data?.body?.companylist);
    const cid = LocalStorageService.get(COMPANY_ID);
    return companies.find((c) => c.companyId === cid) || companies[0];
  }, [data]);

  const brand = hexToRgb(active?.themeColor);
  const asideBg = hexToRgb(active?.asideColor);
  const asideText = hexToRgb(active?.asideTextColor);

  // Layout effects, so the colours are on <html> before the first paint rather than flashing in.
  const brandKey = brand ? rgbToHex(brand) : "";
  useLayoutEffect(() => {
    const rgb = hexToRgb(brandKey);
    return rgb ? setVars(primaryVars(rgb)) : undefined;
  }, [brandKey]);

  const sidebarKey = asideBg ? `${rgbToHex(asideBg)}|${asideText ? rgbToHex(asideText) : ""}` : "";
  useLayoutEffect(() => {
    const [bgHex, textHex] = sidebarKey.split("|");
    const bg = hexToRgb(bgHex);
    if (!bg) return undefined;
    const clear = setVars(sidebarVars(bg, hexToRgb(textHex)));
    document.documentElement.classList.add(SIDEBAR_CLASS);
    return () => {
      clear();
      document.documentElement.classList.remove(SIDEBAR_CLASS);
    };
  }, [sidebarKey]);

  const value = useMemo<BrandingContextType>(() => {
    const bg = hexToRgb(active?.asideColor);
    return {
      companyName: active?.companyName || "",
      logoUrl: active?.logoUrl ? `${ENV.apiEndpoint}/${active.logoUrl}` : "",
      sidebarTheme: bg && luminance(bg) > 0.5 ? "light" : "dark",
    };
  }, [active]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};
